import { Controller } from '@hotwired/stimulus';
import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';

export default class extends Controller {
  static targets = ['menu'];
  static values = {
    placement: { type: String, default: 'bottom' },
    maxWidth: { type: String, default: 'none' },
    transform: String
  };

  connect() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('turbo:click', this.handleTurboClick);
    this.cleanup = null;
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('turbo:click', this.handleTurboClick);
    this.clearHideTimeout();
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
  }

  clearHideTimeout() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  toggle = (event) => {
    event.preventDefault();
    if (this.menuTarget.classList.contains('hidden')) {
      this.show(event.currentTarget);
    } else {
      this.hide();
    }
  }

  handleKeydown = (event) => {
    // Handle Enter and Space keys for keyboard accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle(event);
    }
  }

  show(button) {
    this.clearHideTimeout();
    this.menuTarget.classList.remove('hidden');
    this.menuTarget.classList.add('visible');
    const item = this.element.closest('.photos__item');
    const transform = this.hasTransformValue
      ? this.transformValue : this.menuTarget.style.transform || '';

    if (item) item.classList.add('photos__item--active');

    if (this.cleanup) {
      this.cleanup();
    }

    // Add mouseleave listeners for auto-close
    this.element.addEventListener('mouseleave', this.handleMouseLeave);
    this.menuTarget.addEventListener('mouseleave', this.handleMenuMouseLeave);
    this.menuTarget.addEventListener('mouseenter', this.handleMenuMouseEnter);

    const placement = this.placementValue || 'bottom';

    this.cleanup = autoUpdate(button, this.menuTarget, async () => {
      const middleware = [offset(8), flip(), shift()];
      const { x, y } = await computePosition(button, this.menuTarget, {
        placement,
        middleware,
        strategy: 'fixed',
      });

      // Measure content width for dynamic sizing
      this.menuTarget.style.width = 'auto';
      this.menuTarget.style.maxWidth = 'none';
      const contentWidth = this.menuTarget.scrollWidth;

      // Calculate width with padding
      const calculatedWidth = contentWidth + 10;

      // Apply styles with calculated width
      Object.assign(this.menuTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
        position: 'fixed',
        width: `${calculatedWidth}px`,
        maxWidth: this.maxWidthValue,
        transform: transform
      });
    });
  }

  hide = () => {
    this.clearHideTimeout();
    this.menuTarget.classList.add('hidden');
    this.menuTarget.classList.remove('visible');
    const item = this.element.closest('.photos__item');
    if (item) item.classList.remove('photos__item--active');

    // Remove mouseleave listeners
    this.element.removeEventListener('mouseleave', this.handleMouseLeave);
    this.menuTarget.removeEventListener('mouseleave', this.handleMenuMouseLeave);
    this.menuTarget.removeEventListener('mouseenter', this.handleMenuMouseEnter);

    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }
  }

  handleOutsideClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.hide();
    }
  }

  handleTurboClick = (event) => {
    const link = event.target.closest("a[data-turbo-frame='modal']");
    if (link && this.element.contains(link)) {
      this.hide();
    }
  }

  handleMouseLeave = (event) => {
    // Check if the mouse is moving to the menu
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && this.menuTarget.contains(relatedTarget)) {
      return;
    }
    // Use a small timeout to allow moving to the menu
    this.hideTimeout = setTimeout(() => {
      this.hide();
    }, 100);
  }

  handleMenuMouseLeave = (event) => {
    // Check if the mouse is moving back to the trigger element
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && this.element.contains(relatedTarget)) {
      return;
    }
    this.hide();
  }

  handleMenuMouseEnter = () => {
    // Cancel any pending hide when entering the menu
    this.clearHideTimeout();
  }
}
